import { isLeft } from "fp-ts/lib/These";
import { inject } from "mobx-react";
import { useMemo } from "react";

import { Remote } from "../../../../core/components/remote/Remote.component";
import { VideoPlaylistBlockResolver } from "@ihr-radioedit/inferno-core";
import { isPWSSessionBackend } from "@inferno/renderer-shared-core";
import { getPaths } from "@ihr-radioedit/inferno-core";
import { ILog, getQueryDate, MonthNumber } from "@ihr-radioedit/inferno-core";
import type { PageBlockInterface } from "../../../../core/page-blocks/Block.component";
import { getGetCoastContentItem } from "../../../../core/services/PubSub";
import { getShowZypeSlugs } from "../../lib";
import { CoastShowPayloadResolver } from "../../page-blocks/content/CoastContent.types";
import { CoastMediaPlaylist } from "./CoastMediaPlaylist.component";
import { CoastMediaSkeleton } from "./CoastMediaSkeleton.component";
import type { Store } from "@inferno/renderer-shared-core";
import { coastMediaService } from "../../../../core/services/Pws";

const log = ILog.logger("CoastMediaPlaylistLoader.component");

interface CoastMediaPlaylistLoaderProps extends PageBlockInterface {
  store?: Store;
}

export const CoastMediaPlaylistLoader = inject("store")(({ store, block }: CoastMediaPlaylistLoaderProps) => {
  const session = store?.session.currentSession;

  if (!store || !isPWSSessionBackend(session)) {
    return null;
  }

  const resolvedBlock = VideoPlaylistBlockResolver.decode(block.value);

  if (isLeft(resolvedBlock)) {
    const errorMessage = `Video Playlist Block Failed Validation\n ${getPaths(resolvedBlock).join("\n")}`;
    log.info(errorMessage);
    return null;
  }

  const { year, month, day } = resolvedBlock.right;
  const parsedYear = Number(year);
  const parsedMonth = Number(month);
  const parsedDay = Number(day);

  if (isNaN(parsedYear) || isNaN(parsedMonth) || isNaN(parsedDay)) {
    log.info(`Video Playlist Block invalid params ${year} ${month} ${day}`);
    return null;
  }

  const { zypeSlug = "" } = store.routeParams;
  const showDate = useMemo(
    () => getQueryDate(parsedYear, parsedMonth as MonthNumber, parsedDay),
    [parsedDay, parsedMonth, parsedYear],
  );

  const paddedDay = ("0" + showDate.getDate()).slice(-2);
  const paddedMonth = ("0" + (showDate.getMonth() + 1)).slice(-2);

  const slug = `${year}-${paddedMonth}-${paddedDay}-show`;
  const showTimestamp = useMemo(() => {
    return Date.UTC(
      showDate.getFullYear(),
      showDate.getMonth(),
      showDate.getDate(),
      showDate.getHours(),
      showDate.getMinutes(),
    );
  }, [showDate]);

  return (
    <Remote
      loader={() => getGetCoastContentItem({ type: "content:coast-to-coast-show", slug })}
      cacheKey={`coast-show-${slug}`}
      showLoading={false}
      fallback={<CoastMediaSkeleton />}
    >
      {({ data }) => {
        if (data?.payload) {
          const resolvedPayload = CoastShowPayloadResolver.decode(data.payload);

          if (isLeft(resolvedPayload)) {
            log.error(`Payload failed validation: ${getPaths(resolvedPayload)}`);
            return null;
          }

          const { fields } = resolvedPayload.right;
          const slugs = getShowZypeSlugs(fields);

          if (slugs.length > 0) {
            return (
              <Remote
                loader={() => coastMediaService.getMediaEpisodesBySlugs(slugs, session?.state?.accessToken ?? "")}
                cacheKey={`coast-episodes-playlist-${showTimestamp}`}
                fallback={<CoastMediaSkeleton />}
                showLoading={true}
              >
                {remote =>
                  remote.data?.videos.length ? (
                    <CoastMediaPlaylist
                      playerApiKey={remote.data.playerApiKey}
                      episodesInput={remote.data.videos}
                      session={session}
                      zypeSlug={zypeSlug}
                    />
                  ) : null
                }
              </Remote>
            );
          }
        }

        return <CoastMediaSkeleton />;
      }}
    </Remote>
  );
});

export default CoastMediaPlaylistLoader;
